// src/features/pdf/components/DraggableFileItem.tsx
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ListItem, ListItemIcon, ListItemText, IconButton, Paper, Box } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import { formatBytes } from '../utils/formatter';

interface DraggableFileItemProps {
    file: File;
    id: string;
    onDelete: (id: string) => void;
    onClick: (file: File) => void;
    isDraggable: boolean;
}

const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) {
        return <PictureAsPdfIcon />;
    }
    if (fileType.includes('image')) {
        return <ImageIcon />;
    }
    return <PictureAsPdfIcon />;
};

const DraggableFileItem: React.FC<DraggableFileItemProps> = ({ file, id, onDelete, onClick, isDraggable }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : 1,
        // Clicks should work whether it is draggable or not, but dragging is conditional
        cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            elevation={isDragging ? 4 : 1}
            sx={{
                mb: 1,
                '&:hover': {
                    boxShadow: 3
                },
                // Add a pointer cursor to the whole item to indicate it's clickable
                cursor: 'pointer'
            }}
        >
            <ListItem
                // Use a button component for better accessibility and hover effects
                component="div" // The outer Paper/onClick handles the action
                secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={(e) => { e.stopPropagation(); onDelete(id); }}>
                        <DeleteIcon />
                    </IconButton>
                }
                sx={{
                    bgcolor: 'background.paper',
                }}
                onClick={() => onClick(file)}
            >
                {isDraggable && (
                    // Stop propagation to prevent item click when dragging
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 1, cursor: 'grab' }} {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}>
                        <DragIndicatorIcon />
                    </ListItemIcon>
                )}
                <ListItemIcon sx={{ minWidth: 'auto', mr: 2 }}>{getFileIcon(file.type)}</ListItemIcon>
                <ListItemText
                    primary={file.name}
                    secondary={formatBytes(file.size)}
                />
            </ListItem>
        </Paper>
    );
};

export default DraggableFileItem;